import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectActiveQuestionId,
  selectSkippedQuestionNumbers,
  setActiveQuestionId,
} from '../assessmentsSlice';
import {
  AssessmentsQuestionCardState,
  QuestionCardVariant,
  UseAssessmentsQuestionCardStateProps,
} from '../types';

/**
 * Derives the presentational state for a single question card (skipped/active
 * flags, variant, a11y attributes, and the focus handler).
 *
 * Kept separate from `useAssessmentsSkipLogic` so question cards can subscribe
 * to just the active/skipped state without pulling in the full skip-logic tree.
 */
export const useQuestionCardState = () => {
  const dispatch = useDispatch();
  const activeQuestionId = useSelector(selectActiveQuestionId);
  const skippedQuestionNumbers = useSelector(selectSkippedQuestionNumbers);

  const getQuestionCardState = useCallback(
    ({ id, questionNumber, isSubQuestion, type }: UseAssessmentsQuestionCardStateProps) => {
      const isSkipped = skippedQuestionNumbers.includes(questionNumber);
      const cardVariant: QuestionCardVariant =
        isSkipped || type === 'unsupported' ? 'outlined' : 'elevation';

      const handleCardFocus = () => {
        if (!isSubQuestion && !isSkipped) {
          dispatch(setActiveQuestionId(id));
        }
      };

      return {
        isSkipped,
        isActive: activeQuestionId === id,
        cardVariant,
        cardTabIndex: isSkipped ? -1 : undefined,
        cardAriaDisabled: isSkipped || undefined,
        handleCardFocus,
      } as AssessmentsQuestionCardState;
    },
    [activeQuestionId, skippedQuestionNumbers, dispatch],
  );

  return { getQuestionCardState };
};
